import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/icon_1024.png";
import Magnetic from "../ui/Magnetic";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [active, setActive]     = useState("");
  const visibleRef              = useRef(new Set());
  const { lang, toggle }        = useLang();

  const links = c.nav.links;

  /* ── Scroll backdrop ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Active section via IntersectionObserver ── */
  useEffect(() => {
    const ids = links.map((l) => l.href.slice(1));

    const pick = () => {
      const found = ids.find((id) => visibleRef.current.has(id));
      setActive(found ?? "");
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleRef.current.add(entry.target.id);
          else visibleRef.current.delete(entry.target.id);
        });
        pick();
      },
      { rootMargin: "-74px 0px -55% 0px", threshold: 0 }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [links]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300"
      style={{
        backgroundColor: scrolled ? "rgba(16,15,12,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled
          ? "1px solid rgba(255,255,255,0.07)"
          : "1px solid transparent",
      }}
      data-testid="navbar"
    >
      <div className="flex items-center justify-between py-4 px-6 lg:px-20">
        {/* Logo */}
        <a href="/#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span
            className="relative overflow-hidden rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-500"
            style={{
              width: 46, height: 46,
              boxShadow: "0 0 24px rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.15)",
              background: "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))",
            }}
          >
            <img src={logo} alt="Przestrzeń Apps" className="w-full h-full object-cover scale-110" draggable="false" />
          </span>
          <span className="font-display text-[1.05rem] tracking-[0.04em] text-white-warm">
            Przestrzeń <span className="text-amber italic">Apps</span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => {
            const id = l.href.slice(1);
            const isActive = active === id;
            return (
              <Magnetic key={l.href} radius={72} strength={0.26}>
                <a
                  href={`/${l.href}`}
                  className="font-body text-[13px] tracking-[0.04em] relative pb-1.5"
                  style={{ color: isActive ? "#F2E8D8" : "rgba(242,232,216,0.55)", transition: "color 250ms ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#7CC4D8"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? "#F2E8D8" : "rgba(242,232,216,0.55)"; }}
                  data-testid={`nav-link-${l.pl.toLowerCase()}`}
                >
                  {l[lang]}
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
                      width: 3, height: 3, borderRadius: "50%",
                      background: "#7CC4D8", opacity: isActive ? 1 : 0,
                      transition: "opacity 300ms ease", boxShadow: "0 0 6px rgba(124,196,216,0.7)",
                    }}
                  />
                </a>
              </Magnetic>
            );
          })}

          {/* Lang toggle — sliding pill */}
          <Magnetic radius={60} strength={0.2}>
            <button
              onClick={toggle}
              aria-label="Switch language"
              style={{
                position: "relative", display: "inline-flex", alignItems: "center",
                borderRadius: 999, padding: 3,
                border: "1px solid rgba(124,196,216,0.22)",
                background: "rgba(124,196,216,0.04)",
                backdropFilter: "blur(8px)",
                cursor: "pointer",
                transition: "border-color 300ms ease, box-shadow 300ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(124,196,216,0.45)";
                e.currentTarget.style.boxShadow  = "0 0 18px rgba(124,196,216,0.14)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(124,196,216,0.22)";
                e.currentTarget.style.boxShadow  = "none";
              }}
            >
              {["pl", "en"].map((l) => (
                <span
                  key={l}
                  style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 999, padding: "0.22rem 0.7rem" }}
                >
                  {lang === l && (
                    <motion.span
                      layoutId="lang-indicator"
                      style={{
                        position: "absolute", inset: 0, borderRadius: 999,
                        background: "rgba(124,196,216,0.18)",
                        boxShadow: "0 0 10px rgba(124,196,216,0.20), inset 0 0 0 1px rgba(124,196,216,0.28)",
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 38 }}
                    />
                  )}
                  <span
                    className="font-mono text-[10px] tracking-[0.22em]"
                    style={{
                      position: "relative", zIndex: 1,
                      color: lang === l ? "#7CC4D8" : "rgba(242,232,216,0.32)",
                      transition: "color 250ms ease",
                      userSelect: "none",
                    }}
                  >
                    {l.toUpperCase()}
                  </span>
                </span>
              ))}
            </button>
          </Magnetic>

          <Magnetic>
            <a
              href="/#kontakt"
              className="border border-amber/70 text-amber rounded-full px-5 py-2 text-[12px] tracking-[0.1em] uppercase font-body transition-all duration-300"
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#7CC4D8";
                e.currentTarget.style.color = "#7CC4D8";
                e.currentTarget.style.boxShadow = "0 0 16px rgba(124,196,216,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(200,121,65,0.7)";
                e.currentTarget.style.color = "#C87941";
                e.currentTarget.style.boxShadow = "";
              }}
              data-testid="nav-cta"
            >
              {c.nav.cta[lang]}
            </a>
          </Magnetic>
        </nav>

        {/* Mobile burger */}
        <button
          aria-label="Menu"
          className="md:hidden flex flex-col gap-[5px] p-2"
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-burger"
        >
          <span className="block w-6 h-px bg-white/70 transition-transform" style={{ transform: open ? "rotate(45deg) translateY(7px)" : "none" }} />
          <span className="block w-6 h-px bg-white/70 transition-opacity" style={{ opacity: open ? 0 : 1 }} />
          <span className="block w-6 h-px bg-white/70 transition-transform" style={{ transform: open ? "rotate(-45deg) translateY(-7px)" : "none" }} />
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
            className="md:hidden bg-navy-deep/95 border-t border-glass-border"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => {
                const id = l.href.slice(1);
                return (
                  <a
                    key={l.href}
                    href={`/${l.href}`}
                    onClick={() => setOpen(false)}
                    className="font-display text-2xl transition-colors duration-200"
                    style={{ color: active === id ? "#7CC4D8" : "rgba(242,232,216,0.80)" }}
                  >
                    {l[lang]}
                  </a>
                );
              })}
              {/* Mobile lang toggle */}
              <button
                onClick={toggle}
                aria-label="Switch language"
                style={{
                  alignSelf: "flex-start", display: "inline-flex", alignItems: "center",
                  borderRadius: 999, padding: 3,
                  border: "1px solid rgba(124,196,216,0.25)",
                  background: "rgba(124,196,216,0.04)",
                  cursor: "pointer",
                }}
              >
                {["pl", "en"].map((l) => (
                  <span
                    key={l}
                    style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 999, padding: "0.35rem 0.9rem" }}
                  >
                    {lang === l && (
                      <motion.span
                        layoutId="lang-indicator-mobile"
                        style={{
                          position: "absolute", inset: 0, borderRadius: 999,
                          background: "rgba(124,196,216,0.18)",
                          boxShadow: "inset 0 0 0 1px rgba(124,196,216,0.28)",
                        }}
                        transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      />
                    )}
                    <span
                      className="font-mono text-[11px] tracking-[0.22em]"
                      style={{
                        position: "relative", zIndex: 1,
                        color: lang === l ? "#7CC4D8" : "rgba(242,232,216,0.35)",
                        transition: "color 250ms ease",
                        userSelect: "none",
                      }}
                    >
                      {l.toUpperCase()}
                    </span>
                  </span>
                ))}
              </button>
              <a
                href="/#kontakt"
                onClick={() => setOpen(false)}
                className="border border-amber text-amber rounded-full px-5 py-3 text-sm uppercase tracking-[0.1em] text-center mt-2"
              >
                {c.nav.cta[lang]}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
